/**
 * ArtifactQualityReview - Phase 4 Deliverable Intelligence Engine UI
 * 
 * Provides AI-based quality evaluation for individual DMAIC artifacts.
 * Shows: Quality Score, Strength Level, Risk Level, Improvement Feedback
 * 
 * Tone: Auditor clarity, Coach tone. No gamification.
 */

import { useEffect, useRef, useState } from "react";
import { Button } from "./ui/button";
import {
  Loader2,
  Brain,
  CheckCircle2,
  AlertTriangle,
  XCircle,
  ChevronDown,
  ChevronUp,
  TrendingUp,
  Shield,
  Info,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function serializeArtifactContent(value) {
  if (!value) return "__saved_artifact__";
  try {
    return JSON.stringify(value);
  } catch (error) {
    console.error("Failed to serialize artifact content for evaluation snapshot tracking:", error);
    return "__unserializable_artifact__";
  }
}

export default function ArtifactQualityReview({
  projectId,
  artifactType,
  token,
  artifactContent = null,
  className = "",
  variant = "inline",
  title = "AI Evaluation",
  description = "Evaluate this artifact for Define-phase quality, risks, and next actions.",
  onImprovementAction,
}) {
  const [loading, setLoading] = useState(false);
  const [evaluation, setEvaluation] = useState(null);
  const [expanded, setExpanded] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [lastEvaluatedSnapshot, setLastEvaluatedSnapshot] = useState("");
  const latestArtifactContentRef = useRef(artifactContent);
  const artifactContentSnapshot = serializeArtifactContent(artifactContent);
  const evaluatesLiveDraft = Boolean(artifactContent);

  useEffect(() => {
    latestArtifactContentRef.current = artifactContent;
  }, [artifactContent]);

  const evaluationIsStale = Boolean(
    evaluation && lastEvaluatedSnapshot && lastEvaluatedSnapshot !== artifactContentSnapshot,
  );

  const runEvaluation = async () => {
    setLoading(true);
    setExpanded(true);
    setErrorMessage("");
    const artifactPayload = latestArtifactContentRef.current;
    const payloadSnapshot = serializeArtifactContent(artifactPayload);
    try {
      const res = await fetch(
        `${API}/projects/${projectId}/gate/artifact/evaluate?artifact_type=${artifactType}`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          body: artifactPayload ? JSON.stringify({ artifact_content: artifactPayload }) : undefined,
        }
      );
      if (res.ok) {
        setEvaluation(await res.json());
        setLastEvaluatedSnapshot(payloadSnapshot);
      } else {
        const err = await res.json();
        console.error("Evaluation failed:", err);
        setErrorMessage(err?.detail || err?.message || "Evaluation failed. Try again.");
      }
    } catch (e) {
      console.error("Evaluation error:", e);
      setErrorMessage("Evaluation failed. Check your connection and try again.");
    } finally {
      setLoading(false);
    }
  };

  // Get strength badge style
  const getStrengthStyle = (level) => {
    switch (level) {
      case "Strong":
        return { bg: "bg-emerald-500/20", text: "text-emerald-400", border: "border-emerald-500/30" };
      case "Adequate":
        return { bg: "bg-amber-500/20", text: "text-amber-400", border: "border-amber-500/30" };
      case "Weak":
        return { bg: "bg-red-500/20", text: "text-red-400", border: "border-red-500/30" };
      default:
        return { bg: "bg-slate-500/20", text: "text-slate-400", border: "border-slate-500/30" };
    }
  };

  // Get risk badge style
  const getRiskStyle = (level) => {
    switch (level) {
      case "Low":
        return "bg-emerald-400/20 text-emerald-400";
      case "Moderate":
        return "bg-amber-400/20 text-amber-400";
      case "High":
        return "bg-red-400/20 text-red-400";
      default:
        return "bg-slate-400/20 text-slate-400";
    }
  };

  // Get score color
  const getScoreColor = (score) => {
    if (score >= 90) return "text-emerald-400";
    if (score >= 70) return "text-amber-400";
    return "text-red-400";
  };

  // Priority badge style
  const getPriorityStyle = (priority) => {
    switch (priority) {
      case "critical":
        return "bg-red-500/20 text-red-400";
      case "high":
        return "bg-orange-500/20 text-orange-400";
      case "moderate":
        return "bg-yellow-500/20 text-yellow-400";
      case "medium":
        return "bg-amber-500/20 text-amber-400";
      case "low":
        return "bg-slate-500/20 text-slate-400";
      default:
        return "bg-slate-500/20 text-slate-400";
    }
  };

  const isPanelVariant = variant === "panel";
  const overallToneClassName = evaluation
    ? getStrengthStyle(evaluation.strength_level).text
    : "text-muted-foreground";
  const priorityOrder = { critical: 0, high: 1, moderate: 2, medium: 3, low: 4 };
  const rankedImprovementFeedback = [...(evaluation?.improvement_feedback || [])].sort((a, b) => {
    const aScore = priorityOrder[a?.priority] ?? 99;
    const bScore = priorityOrder[b?.priority] ?? 99;
    if (aScore !== bScore) return aScore - bScore;
    return (a?.criterion || "").localeCompare(b?.criterion || "");
  });

  const renderImprovementFeedback = (items = [], mode = "inline") => {
    if (items.length === 0) return null;

    const primaryItem = mode === "panel" ? items[0] : null;
    const secondaryItems = mode === "panel" ? items.slice(1) : items;

    return (
      <div>
        <div className={`${mode === "panel" ? "mb-2.5 flex items-center justify-between gap-3" : "mb-1.5"}`}>
          <p className={`font-medium uppercase tracking-wider ${mode === "panel" ? "text-[11px] text-amber-300" : "text-[10px] text-amber-400"}`}>
            {mode === "panel" ? "What To Fix Next" : "What Must Be Improved"}
          </p>
          {mode === "panel" && items.length > 0 ? (
            <span className="text-[10px] font-medium uppercase tracking-wider text-muted-foreground">
              {items.length} priority item{items.length === 1 ? "" : "s"}
            </span>
          ) : null}
        </div>

        {mode === "panel" && primaryItem ? (
          <div className="mb-2.5 rounded-xl border border-amber-500/25 bg-amber-500/8 p-3.5">
            <div className="mb-1.5 flex flex-wrap items-center gap-2">
              <span className="rounded-full bg-amber-500/15 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-amber-300">
                Highest Value Fix
              </span>
              <span className="text-sm font-semibold">{primaryItem.criterion}</span>
              <span className={`px-1.5 py-0.5 text-[9px] font-medium rounded ${getPriorityStyle(primaryItem.priority)}`}>
                {primaryItem.priority?.toUpperCase()}
              </span>
            </div>
            <p className="mb-1.5 text-sm text-muted-foreground">{primaryItem.issue}</p>
            <p className="text-sm text-primary">
              <span className="font-medium">Action: </span>{primaryItem.action}
            </p>
            {onImprovementAction && (
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="mt-3 h-8 w-full justify-center gap-1.5 text-[11px]"
                onClick={() => onImprovementAction(primaryItem)}
              >
                Jump to section
              </Button>
            )}
          </div>
        ) : null}

        <div className="space-y-2">
          {secondaryItems.map((item, i) => (
            <div
              key={i}
              className={`${mode === "panel" ? "rounded-lg border border-border/70 bg-background/50 p-2.5" : "rounded border border-border bg-background p-2"}`}
            >
              <div className="mb-1 flex flex-wrap items-center gap-2">
                {mode === "panel" ? (
                  <span className="text-[10px] font-semibold text-muted-foreground">{String(i + 2).padStart(2, "0")}</span>
                ) : null}
                <span className={`${mode === "panel" ? "text-xs" : "text-xs"} font-medium`}>{item.criterion}</span>
                <span className={`px-1.5 py-0.5 text-[9px] font-medium rounded ${getPriorityStyle(item.priority)}`}>
                  {item.priority?.toUpperCase()}
                </span>
              </div>
              <p className={`${mode === "panel" ? "text-xs" : "text-xs"} text-muted-foreground mb-1`}>{item.issue}</p>
              <p className={`${mode === "panel" ? "text-xs" : "text-xs"} text-primary`}>
                <span className="font-medium">Action: </span>{item.action}
              </p>
              {mode === "panel" && onImprovementAction && (
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="mt-1.5 h-6 px-1.5 text-[10px] text-primary hover:text-primary"
                  onClick={() => onImprovementAction(item)}
                >
                  Jump to section
                </Button>
              )}
            </div>
          ))}
        </div>
      </div>
    );
  };

  const renderScoringBreakdown = (mode = "inline") => (
    <div>
      <p className={`font-medium uppercase tracking-wider ${mode === "panel" ? "mb-2 text-[11px] text-muted-foreground" : "mb-1.5 text-[10px] text-muted-foreground"}`}>
        Score Breakdown
      </p>
      <div className="space-y-1.5">
        {Object.entries(evaluation.scoring_breakdown || {}).map(([criterion, data]) => (
          <div key={criterion} data-testid={`breakdown-${criterion}`}>
            <div className="flex items-center gap-2">
              <div className="flex-1">
                <div className={`flex items-center justify-between ${mode === "panel" ? "text-xs" : "text-[11px]"}`}>
                  <span className="capitalize text-muted-foreground">{criterion.replace(/_/g, " ")}</span>
                  <div className="flex items-center gap-1.5">
                    {data.context_adjusted && (
                      <span className="text-[9px] text-blue-400 line-through">{data.raw_score}%</span>
                    )}
                    <span className={getScoreColor(data.score)}>{data.score}%</span>
                  </div>
                </div>
                <div className={`overflow-hidden rounded-full bg-slate-700 ${mode === "panel" ? "h-1.5" : "h-1"}`}>
                  <div
                    className={`h-full rounded-full ${data.score >= 70 ? "bg-emerald-400" : data.score >= 50 ? "bg-amber-400" : "bg-red-400"}`}
                    style={{ width: `${data.score}%` }}
                  />
                </div>
              </div>
              <span className="w-6 text-right text-[9px] text-muted-foreground">{data.weight}%</span>
            </div>
            {data.adjustment_detail && (
              <p className="mt-0.5 ml-1 flex items-center gap-1 text-[9px] text-blue-400/80">
                <Info className="h-2.5 w-2.5 shrink-0" />
                +{data.adjustment_detail.to}% - {data.adjustment_detail.reason}
              </p>
            )}
          </div>
        ))}
      </div>
    </div>
  );

  if (isPanelVariant && !evaluation && !loading) {
    return (
      <div className={`rounded-2xl border border-border bg-card/95 p-5 shadow-sm ${className}`} data-testid={`quality-review-panel-${artifactType}`}>
        <div className="space-y-1 border-b border-border/60 pb-4">
          <div className="flex items-center gap-2">
            <Brain className="h-4 w-4 text-primary" />
            <h3 className="font-heading text-base font-semibold">{title}</h3>
          </div>
          <p className="text-sm text-muted-foreground">{description}</p>
        </div>
        <div className="space-y-4 pt-4">
          <div className="rounded-xl border border-dashed border-border/70 bg-background/40 p-4 text-sm text-muted-foreground">
            Run an evaluation to see the current score, the most important gaps, and recommended next fixes for this artifact.
            {evaluatesLiveDraft ? (
              <p className="mt-2 text-xs text-primary/80">
                Uses the current draft, including unsaved edits.
              </p>
            ) : null}
          </div>
          {errorMessage ? (
            <div className="rounded-lg border border-red-500/25 bg-red-500/10 px-3 py-2 text-xs text-red-200">
              {errorMessage}
            </div>
          ) : null}
          <Button
            onClick={runEvaluation}
            className="w-full gap-2"
            data-testid={`quality-review-btn-${artifactType}`}
          >
            <Brain className="h-4 w-4" />
            Run AI Evaluation
          </Button>
        </div>
      </div>
    );
  }

  if (isPanelVariant && loading) {
    return (
      <div className={`rounded-2xl border border-border bg-card/95 p-5 shadow-sm ${className}`} data-testid={`quality-review-panel-${artifactType}`}>
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" />
          Evaluating artifact quality...
        </div>
      </div>
    );
  }

  if (!evaluation && !loading) {
    return (
      <div className={`space-y-1.5 ${className}`}>
        <Button
          onClick={runEvaluation}
          variant="ghost"
          size="sm"
          className="gap-1.5 text-xs text-muted-foreground hover:text-primary"
          data-testid={`quality-review-btn-${artifactType}`}
        >
          <Brain className="w-3.5 h-3.5" />
          AI Quality Review
        </Button>
        {evaluatesLiveDraft ? (
          <p className="text-[10px] text-muted-foreground">Evaluates the current draft, including unsaved edits.</p>
        ) : null}
        {errorMessage ? (
          <p className="text-[10px] text-red-300">{errorMessage}</p>
        ) : null}
      </div>
    );
  }

  if (loading) {
    return (
      <div className={`flex items-center gap-2 text-sm text-muted-foreground ${className}`}>
        <Loader2 className="w-4 h-4 animate-spin" />
        Evaluating...
      </div>
    );
  }

  const strengthStyle = getStrengthStyle(evaluation.strength_level);

  if (isPanelVariant) {
    return (
      <div className={`rounded-2xl border border-border bg-card/95 p-4 shadow-sm ${className}`} data-testid={`quality-review-result-${artifactType}`}>
        <div className="space-y-1 border-b border-border/60 pb-4">
          <div className="flex items-center gap-2">
            <Brain className="h-4 w-4 text-primary" />
            <h3 className="font-heading text-base font-semibold">{title}</h3>
          </div>
          <p className="text-sm text-muted-foreground">{description}</p>
        </div>

        <div className="space-y-4 pt-4">
          <div className="rounded-xl border border-border/70 bg-background/60 p-3.5">
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div className="space-y-1">
                <p className="text-[11px] font-semibold uppercase tracking-[0.2em] text-muted-foreground">Overall Status</p>
                <div className="flex items-center gap-3">
                  <span className={`text-3xl font-bold ${getScoreColor(evaluation.quality_score)}`}>
                    {evaluation.quality_score}
                  </span>
                  <div className="space-y-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className={`rounded-full border px-2.5 py-1 text-[11px] font-semibold uppercase tracking-wider ${strengthStyle.bg} ${strengthStyle.border} ${overallToneClassName}`}>
                        {evaluation.strength_level}
                      </span>
                      <span className={`rounded-full px-2.5 py-1 text-[11px] font-semibold uppercase tracking-wider ${getRiskStyle(evaluation.risk_level)}`}>
                        {evaluation.risk_level} Risk
                      </span>
                      {evaluation.context_adjustments_applied && (
                        <span className="rounded-full bg-blue-500/15 px-2.5 py-1 text-[11px] font-semibold uppercase tracking-wider text-blue-400">
                          Context Adjusted
                        </span>
                      )}
                      {evaluationIsStale && (
                        <span className="rounded-full border border-amber-500/25 bg-amber-500/10 px-2.5 py-1 text-[11px] font-semibold uppercase tracking-wider text-amber-300">
                          Draft Updated
                        </span>
                      )}
                    </div>
                    <p className="text-xs text-muted-foreground">
                      Confidence: {evaluation.confidence_level}
                    </p>
                  </div>
                </div>
              </div>
              <Button onClick={runEvaluation} variant="outline" size="sm" className="gap-1.5 self-start">
                <Brain className="h-3.5 w-3.5" />
                Re-evaluate
              </Button>
            </div>
          </div>

          {evaluation.scope_label && (
            <div className="flex items-start gap-2 rounded-lg border border-border bg-muted/25 px-3 py-2" data-testid={`artifact-scope-label-${artifactType}`}>
              <Info className="mt-0.5 h-3.5 w-3.5 shrink-0 text-muted-foreground" />
              <p className="text-xs leading-relaxed text-muted-foreground">{evaluation.scope_label}</p>
            </div>
          )}

          {evaluationIsStale ? (
            <div className="flex items-start gap-2 rounded-lg border border-amber-500/20 bg-amber-500/5 px-3 py-2">
              <Info className="mt-0.5 h-3.5 w-3.5 shrink-0 text-amber-300" />
              <p className="text-xs leading-relaxed text-amber-100">
                The Problem page changed after this score was calculated. Re-evaluate to score the latest content.
              </p>
            </div>
          ) : null}

          {errorMessage ? (
            <div className="rounded-lg border border-red-500/25 bg-red-500/10 px-3 py-2 text-xs text-red-200">
              {errorMessage}
            </div>
          ) : null}

          {renderImprovementFeedback(rankedImprovementFeedback, "panel")}

          {evaluation.strengths?.length > 0 && (
            <div>
              <p className="mb-1.5 text-[10px] font-medium uppercase tracking-wider text-emerald-300/90">Strengths</p>
              <ul className="space-y-1.5">
                {evaluation.strengths.map((s, i) => (
                  <li key={i} className="flex items-start gap-2 text-xs text-muted-foreground">
                    <CheckCircle2 className="mt-0.5 h-3.5 w-3.5 shrink-0 text-emerald-400" />
                    {s}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {renderScoringBreakdown("panel")}

          {evaluation.context_notes?.length > 0 && (
            <div className="space-y-1.5 rounded-lg border border-blue-500/20 bg-blue-500/5 p-2.5" data-testid={`artifact-context-notes-${artifactType}`}>
              {evaluation.context_notes.map((note, i) => (
                <p key={i} className="flex items-start gap-1.5 text-xs text-blue-400">
                  <Info className="mt-0.5 h-3 w-3 shrink-0" />
                  {note}
                </p>
              ))}
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className={`space-y-2 ${className}`} data-testid={`quality-review-result-${artifactType}`}>
      {/* Collapsed Header */}
      <button
        onClick={() => setExpanded(!expanded)}
        className={`w-full flex items-center justify-between p-2 rounded-lg ${strengthStyle.bg} border ${strengthStyle.border} transition-colors hover:opacity-90`}
        data-testid={`quality-review-toggle-${artifactType}`}
      >
        <div className="flex items-center gap-3">
          {evaluation.strength_level === "Strong" && <CheckCircle2 className={`w-4 h-4 ${strengthStyle.text}`} />}
          {evaluation.strength_level === "Adequate" && <AlertTriangle className={`w-4 h-4 ${strengthStyle.text}`} />}
          {evaluation.strength_level === "Weak" && <XCircle className={`w-4 h-4 ${strengthStyle.text}`} />}
          
          <span className={`text-sm font-medium ${strengthStyle.text}`}>
            {evaluation.strength_level}
          </span>
          
          <div className="flex items-center gap-1">
            <TrendingUp className="w-3 h-3 text-muted-foreground" />
            <span className={`text-sm font-bold ${getScoreColor(evaluation.quality_score)}`}>
              {evaluation.quality_score}
            </span>
            <span className="text-xs text-muted-foreground">/100</span>
          </div>
          
          <span className={`px-1.5 py-0.5 text-[10px] font-medium rounded ${getRiskStyle(evaluation.risk_level)}`}>
            <Shield className="w-2.5 h-2.5 inline mr-0.5" />
            {evaluation.risk_level} Risk
          </span>
          
          {evaluation.context_adjustments_applied && (
            <span className="px-1.5 py-0.5 text-[10px] font-medium rounded bg-blue-500/15 text-blue-400" data-testid={`context-adjusted-badge-${artifactType}`}>
              Context Adjusted
            </span>
          )}
          {evaluationIsStale && (
            <span className="px-1.5 py-0.5 text-[10px] font-medium rounded border border-amber-500/25 bg-amber-500/10 text-amber-300">
              Draft Updated
            </span>
          )}
        </div>
        
        {expanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
      </button>

      {/* Expanded Details */}
      {expanded && (
        <div className="space-y-3 p-3 bg-card border border-border rounded-lg animate-in fade-in duration-200">
          {/* Artifact-Level Scope Label */}
          {evaluation.scope_label && (
            <div className="flex items-start gap-2 px-3 py-2 bg-muted/40 border border-border rounded-lg" data-testid={`artifact-scope-label-${artifactType}`}>
              <Info className="w-3.5 h-3.5 text-muted-foreground mt-0.5 shrink-0" />
              <p className="text-[10px] text-muted-foreground leading-relaxed">
                {evaluation.scope_label}
              </p>
            </div>
          )}

          {evaluationIsStale && (
            <div className="flex items-start gap-2 px-3 py-2 bg-amber-500/5 border border-amber-500/20 rounded-lg">
              <Info className="w-3.5 h-3.5 text-amber-300 mt-0.5 shrink-0" />
              <p className="text-[10px] leading-relaxed text-amber-100">
                The draft changed after this evaluation. Re-evaluate to score the latest content.
              </p>
            </div>
          )}

          {errorMessage ? (
            <div className="px-3 py-2 bg-red-500/10 border border-red-500/25 rounded-lg text-[10px] text-red-200">
              {errorMessage}
            </div>
          ) : null}

          {/* Cross-Artifact Context Notes */}
          {evaluation.context_notes?.length > 0 && (
            <div className="px-3 py-2 bg-blue-500/5 border border-blue-500/20 rounded-lg space-y-1" data-testid={`artifact-context-notes-${artifactType}`}>
              {evaluation.context_notes.map((note, i) => (
                <p key={i} className="text-[10px] text-blue-400 flex items-start gap-1.5">
                  <Info className="w-3 h-3 shrink-0 mt-0.5" />
                  {note}
                </p>
              ))}
            </div>
          )}

          {/* Strengths */}
          {evaluation.strengths?.length > 0 && (
            <div>
              <p className="text-[10px] font-medium text-emerald-400 uppercase tracking-wider mb-1.5">
                What is Strong
              </p>
              <ul className="space-y-1">
                {evaluation.strengths.map((s, i) => (
                  <li key={i} className="flex items-start gap-1.5 text-xs text-muted-foreground">
                    <CheckCircle2 className="w-3 h-3 text-emerald-400 mt-0.5 flex-shrink-0" />
                    {s}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Improvement Feedback */}
          {evaluation.improvement_feedback?.length > 0 && (
            renderImprovementFeedback(evaluation.improvement_feedback, "inline")
          )}

          {/* Scoring Breakdown */}
          {renderScoringBreakdown("inline")}

          {/* Confidence & Assumptions */}
          <div className="flex items-center gap-3 pt-2 border-t border-border">
            <div className="flex items-center gap-1 text-[10px] text-muted-foreground">
              <Info className="w-3 h-3" />
              Confidence: {evaluation.confidence_level}
            </div>
          </div>

          {/* Re-run Button */}
          <Button
            onClick={runEvaluation}
            variant="ghost"
            size="sm"
            className="w-full text-xs gap-1"
          >
            <Brain className="w-3 h-3" />
            Re-evaluate
          </Button>
        </div>
      )}
    </div>
  );
}
